import React, { useEffect, useMemo } from 'react';
import type { CardPreviewPaneProps, GalleryAsset } from '../GenericPreviewPane';
import { AssetContainer, GenericPreviewPane } from '../GenericPreviewPane';
import { CardPreviewFavoriteButton } from '../CardCustomizationContent/CardPreviewFavoriteButton';
import { useCardPreviewPaneAssets } from '../useCardPreviewPaneAssets';
import { PremiumPDPReveal } from './PremiumPDPReveal';
import { CardPhoneFrame } from './CardPhoneFrame';
import { useSsrPreviewRenderDelay } from '../CardCustomizationContent/useSsrPreviewRenderDelay';
import { PreviewThumbnail } from '../CardCustomizationContent/PreviewThumbnail';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { FitScaler } from '@apps/card/routes/CardCustomizer/components/FitScaler';
import { Shadow } from '@apps/card/components/Shadow';
import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { getDimensions } from '@apps/card/utils';
import { Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { usePdpColorways } from '../../PdpColorwayContext';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { isBasicDigitalV2 } from '@apps/card/components/ProductUIConfig/isBasicDigitalV2';

export const CardPreviewPane = (props: CardPreviewPaneProps) => {
  const { tq } = useTranslation('stationery');
  const { cardJson, cardFront, cardShape, thumbnailUrl, eventId, templateId, isFavorite, themeId, templateCategory } = props;
  const { cssVarStyle, preloadImages, selectedColorwayId } = usePdpColorways();
  // Premium Digital and Basic Digital V2 share this pane; the active product picks the image set.
  const { activeProduct } = useStationeryProductPricingContext();
  const extraAssets = useCardPreviewPaneAssets('wedding', templateCategory, 'joy_digital', { productType: activeProduct.productType });
  // The envelope reveal is Premium Digital's preview; Basic Digital (V2) shows the card on a phone.
  const showEnvelopeReveal = !isBasicDigitalV2(activeProduct);
  const { canRenderPreview, showsThumbnail } = useSsrPreviewRenderDelay({ cardFront, thumbnailUrl, selectedColorwayId });

  useEffect(() => {
    preloadImages?.();
  }, [preloadImages]);

  const previewContent = useMemo(() => {
    if (!showEnvelopeReveal) {
      // `Page` prefers the front page's own dimensions; these are its fallback.
      const { width, height } = getDimensions(cardJson.card);
      return (
        <Flex
          justifyContent="center"
          width="100%"
          style={cssVarStyle}
        >
          {/* Sized by width: a square tile shrank the phone until it stopped reading as one. */}
          <CardPhoneFrame>
            {/* 24px keeps the paper shadow (~21px once scaled) inside the screen. */}
            {showsThumbnail ? (
              <PreviewThumbnail
                src={thumbnailUrl}
                alt={tq`Front of the Card`}
                cardShape={cardShape}
                widthInches={width}
                heightInches={height}
                fit="contain"
                padding={24}
              />
            ) : (
              <FitScaler padding={24}>
                <Shadow
                  shape={cardShape}
                  shadow={BOX_SHADOW_8_14_26}
                >
                  <Page
                    width={width}
                    height={height}
                    page={canRenderPreview ? cardFront : { ...cardFront, layers: [] }}
                    shape={cardShape}
                    format={StationeryDraftFormat.digital}
                    forcePlaceholderQRCode
                  />
                </Shadow>
              </FitScaler>
            )}
          </CardPhoneFrame>
        </Flex>
      );
    }
    return (
      <Flex
        flexDirection="column"
        marginY={7}
        marginX="auto"
        width="100%"
        alignItems="center"
        marginTop={-32}
      >
        <PremiumPDPReveal
          cardJSON={cardJson}
          cssVarStyle={cssVarStyle}
        />
      </Flex>
    );
  }, [cardJson, cardFront, cardShape, canRenderPreview, showsThumbnail, thumbnailUrl, tq, cssVarStyle, showEnvelopeReveal]);

  const assets: Array<GalleryAsset> = useMemo(() => {
    return [
      {
        render: () => {
          return (
            <AssetContainer
              key="cardFront"
              label={tq`Front of the Card`}
              // The envelope reveal is laid out for a square tile; the phone sets its own height.
              enforceAspectRatio={showEnvelopeReveal}
              badge={
                <CardPreviewFavoriteButton
                  themeId={themeId}
                  templateCategory={templateCategory}
                  eventId={eventId || ''}
                  templateId={templateId}
                  isFavorite={isFavorite}
                />
              }
            >
              {previewContent}
            </AssetContainer>
          );
        },
        thumbnailUrl: thumbnailUrl,
      },
      ...extraAssets,
    ];
  }, [tq, thumbnailUrl, extraAssets, themeId, templateCategory, eventId, templateId, isFavorite, previewContent, showEnvelopeReveal]);

  return (
    <GenericPreviewPane
      {...props}
      assets={assets}
    />
  );
};
